import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { http } from "@/lib/api";
import { readLocation } from "@/components/LocationPill";
import { Warning, X, MapPin, ArrowRight } from "@phosphor-icons/react";

/**
 * LocalAlertBanner — site-wide popup that shows severe/warning weather
 * alerts near the user's chosen location. Dismiss is remembered per-alert
 * (localStorage), so it never nags after the user acknowledges an alert.
 *
 * - Reads location from `readLocation()` (set via LocationPill; falls
 *   back to Portland, OR default).
 * - Polls `/api/weather/alerts/local?lat=&lon=&radius_km=250` every 10 min.
 * - Renders nothing when there are no unseen severe/warning alerts.
 * - Hidden on the /notifications route (users are already looking at
 *   the full feed there) and admin surfaces.
 */
const DISMISS_KEY = "np:weather_alert_dismissed";

function readDismissed() {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); }
  catch { return new Set(); }
}
function writeDismissed(set) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...set].slice(-100))); }
  catch { /* ignore */ }
}

const SUPPRESS_ROUTES = ["/notifications", "/admin", "/steward"];

export default function LocalAlertBanner() {
  const { pathname } = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loc, setLoc] = useState(readLocation);
  const suppressed = SUPPRESS_ROUTES.some((r) => pathname.startsWith(r));

  // Keep loc in sync with LocationPill changes.
  useEffect(() => {
    const onChange = (e) => setLoc(e.detail || readLocation());
    window.addEventListener("np:location-changed", onChange);
    return () => window.removeEventListener("np:location-changed", onChange);
  }, []);

  useEffect(() => {
    if (!loc || !loc.lat || !loc.lon || suppressed) { setAlerts([]); return; }
    let alive = true;
    const dismissed = readDismissed();
    const pull = async () => {
      try {
        const { data } = await http.get(
          `/weather/alerts/local?lat=${loc.lat}&lon=${loc.lon}&radius_km=250&limit=6`,
        );
        if (!alive) return;
        const filtered = (data.alerts || [])
          .filter((a) => a.severity === "severe" || a.severity === "warning")
          .filter((a) => !dismissed.has(a.id));
        setAlerts(filtered);
      } catch { /* silent — banner is optional */ }
    };
    pull();
    const t = setInterval(pull, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [loc, suppressed]);

  if (!alerts.length) return null;

  const dismissOne = (id) => {
    const s = readDismissed(); s.add(id); writeDismissed(s);
    setAlerts((cur) => cur.filter((a) => a.id !== id));
  };
  const dismissAll = () => {
    const s = readDismissed(); alerts.forEach((a) => s.add(a.id)); writeDismissed(s);
    setAlerts([]);
  };

  const first = alerts[0];
  const severeCount = alerts.filter((a) => a.severity === "severe").length;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-[100] w-[min(95vw,640px)]"
      data-testid="local-alert-banner"
    >
      <div
        className={
          "rounded-2xl border shadow-lg backdrop-blur-md overflow-hidden " +
          (severeCount > 0
            ? "border-red-300 bg-red-50/95"
            : "border-amber-300 bg-amber-50/95")
        }
      >
        <div className="flex items-start gap-3 p-3.5">
          <div className={"shrink-0 mt-0.5 " + (severeCount > 0 ? "text-red-700" : "text-amber-700")}>
            <Warning size={18} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap font-mono-tech text-[9px] tracking-[0.28em] uppercase">
              <span className={severeCount > 0 ? "text-red-800" : "text-amber-800"}>
                {severeCount > 0 ? "Severe weather nearby" : "Weather advisory nearby"}
              </span>
              {loc?.city && (
                <span className="inline-flex items-center gap-0.5 text-[#3A3830]">
                  <MapPin size={10} weight="fill" /> {loc.city}{loc.state ? `, ${loc.state}` : ""}
                </span>
              )}
              <span className="text-[#8E8B7F]">· {alerts.length} active</span>
            </div>
            <Link
              to={first.url || "/notifications"}
              className="mt-1 block font-serif-display text-lg leading-tight text-[#241209] hover:text-[color:var(--np-brand,#E4572E)]"
              data-testid="local-alert-primary"
            >
              {first.title}
            </Link>
            {first.excerpt && (
              <div className="mt-0.5 text-xs text-[#3A3830] line-clamp-2">{first.excerpt}</div>
            )}
            {expanded && alerts.length > 1 && (
              <ul className="mt-2 border-t border-black/10 pt-2 space-y-1.5" data-testid="local-alert-more">
                {alerts.slice(1).map((a) => (
                  <li key={a.id} className="flex items-start justify-between gap-2">
                    <Link to={a.url || "/notifications"} className="text-xs text-[#241209] hover:text-[color:var(--np-brand,#E4572E)]">
                      <span className={"inline-block rounded-full font-mono-tech text-[8px] tracking-[0.22em] uppercase px-1.5 mr-1 " + (a.severity === "severe" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800")}>{a.severity}</span>
                      {a.title}
                    </Link>
                    <button
                      onClick={() => dismissOne(a.id)}
                      aria-label="Dismiss"
                      className="text-[#8E8B7F] hover:text-[#241209] shrink-0"
                    ><X size={12} weight="bold" /></button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <Link
                to="/notifications"
                className="inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#E4572E)] hover:underline"
                data-testid="local-alert-see-all"
              >
                Open feed <ArrowRight size={10} weight="bold" />
              </Link>
              {alerts.length > 1 && (
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830] hover:text-[#1B4332]"
                  data-testid="local-alert-expand"
                >
                  {expanded ? "Show less" : `+${alerts.length - 1} more`}
                </button>
              )}
              <button
                onClick={dismissAll}
                className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8E8B7F] hover:text-[#3A3830] ml-auto"
                data-testid="local-alert-dismiss-all"
              >
                Dismiss all
              </button>
            </div>
          </div>
          <button
            onClick={() => dismissOne(first.id)}
            aria-label="Dismiss"
            data-testid="local-alert-close"
            className="shrink-0 text-[#3A3830] hover:text-[#1B4332]"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
